import {
  FormModal, FormField, FormInput, FormSelect 
} from '@/components/ui/form-modal';
import {
  USER_ROLE_LABELS,
  INTERNAL_ROLES,
  SUPPLIER_ROLES,
  type UserRole,
  type InviteUserRequest,
} from '@/features/settings';

const ROLE_OPTIONS = INTERNAL_ROLES.map(role => ({
  value: role,
  label: USER_ROLE_LABELS[role],
}));

const SUPPLIER_ROLE_OPTIONS = SUPPLIER_ROLES.map(role => ({
  value: role,
  label: USER_ROLE_LABELS[role],
}));

const LANGUAGE_OPTIONS = [
  {
    value: 'en',
    label: 'English' 
  },
  {
    value: 'es',
    label: 'Spanish' 
  },
  {
    value: 'fr',
    label: 'French' 
  },
  {
    value: 'de',
    label: 'German' 
  },
];

interface InviteUserModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onInvite: () => Promise<void>;
  readonly inviteType: 'internal' | 'supplier';
  readonly formData: InviteUserRequest;
  readonly setFormData: (data: InviteUserRequest) => void;
  readonly isSubmitting: boolean;
  readonly supplierOptions: Array<{
    value: string;
    label: string 
  }>;
  readonly siteOptions: Array<{
    value: string;
    label: string 
  }>;
}

export function InviteUserModal({
  isOpen,
  onClose,
  onInvite,
  inviteType,
  formData,
  setFormData,
  isSubmitting,
  supplierOptions,
  siteOptions,
}: InviteUserModalProps) {
  const description = inviteType === 'internal' 
    ? 'Send an invitation to join your organization'
    : 'Invite a user from a supplier organization';

  return (
    <FormModal
      title={inviteType === 'internal' ? 'Invite Team Member' : 'Invite Supplier User'}
      description={description}
      isOpen={isOpen}
      onClose={onClose}
      onSave={onInvite}
      saveLabel={isSubmitting ? 'Sending...' : 'Send Invitation'}
      saveDisabled={isSubmitting || !formData.email || !formData.firstName || !formData.lastName}
    >
      <div className="grid gap-4 md:grid-cols-2">
        <FormField label="First Name" required>
          <FormInput
            value={formData.firstName}
            onChange={(e) => setFormData({
              ...formData,
              firstName: e.target.value 
            })}
            placeholder="John"
          />
        </FormField>
        <FormField label="Last Name" required>
          <FormInput
            value={formData.lastName}
            onChange={(e) => setFormData({
              ...formData,
              lastName: e.target.value 
            })}
            placeholder="Doe"
          />
        </FormField>
        <FormField label="Email" required>
          <FormInput
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({
              ...formData,
              email: e.target.value 
            })}
            placeholder="john.doe@example.com"
          />
        </FormField>
        <FormField label="Phone">
          <FormInput
            type="tel"
            value={formData.phoneNumber ?? ''}
            onChange={(e) => setFormData({
              ...formData,
              phoneNumber: e.target.value 
            })}
            placeholder="+1 555 123 4567"
          />
        </FormField>
        <FormField label="Job Title">
          <FormInput
            value={formData.jobTitle ?? ''}
            onChange={(e) => setFormData({
              ...formData,
              jobTitle: e.target.value 
            })}
            placeholder="Executive Chef"
          />
        </FormField>
        <FormField label="Department">
          <FormInput
            value={formData.department ?? ''}
            onChange={(e) => setFormData({
              ...formData,
              department: e.target.value 
            })}
            placeholder="Culinary"
          />
        </FormField>
        <FormField label="Role" required>
          <FormSelect
            value={formData.role}
            onChange={(e) => setFormData({
              ...formData,
              role: e.target.value as UserRole 
            })}
            options={inviteType === 'internal' ? ROLE_OPTIONS : SUPPLIER_ROLE_OPTIONS}
          />
        </FormField>
        <FormField label="Language">
          <FormSelect
            value={formData.preferredLanguage ?? 'en'}
            onChange={(e) => setFormData({
              ...formData,
              preferredLanguage: e.target.value 
            })}
            options={LANGUAGE_OPTIONS}
          />
        </FormField>
        
        {inviteType === 'supplier' && (
          <FormField label="Supplier" required className="md:col-span-2">
            <FormSelect
              value={formData.supplierId ?? ''}
              onChange={(e) => setFormData({
                ...formData,
                supplierId: e.target.value 
              })}
              options={[{
                value: '',
                label: 'Select a supplier...' 
              }, ...supplierOptions]}
            />
          </FormField>
        )}
        
        {inviteType === 'internal' && !['admin', 'finance', 'buyer'].includes(formData.role) && (
          <FormField label="Site Access" className="md:col-span-2">
            <FormSelect
              value={formData.siteAccess?.[0] ?? ''}
              onChange={(e) => setFormData({
                ...formData,
                siteAccess: e.target.value ? [e.target.value] : [] 
              })}
              options={[{
                value: '',
                label: 'All sites (organization-wide)' 
              }, ...siteOptions]}
            />
          </FormField>
        )}
      </div>
    </FormModal>
  );
}
